<template>
  <div class="page-shell">
    <p
      class="mobile-page-title font-helvetica-700 font-bold text-gray-dark lg:text-[40px] lg:uppercase"
    >
      Профиль
    </p>
    <SettingsNavBar />

    <div class="flex w-full flex-col gap-5 rounded-[20px] bg-white-gray p-6">
      <p class="font-helvetica-700 text-[20px] font-bold text-gray-dark">
        Добавить новый адрес
      </p>

      <form
        class="flex flex-wrap items-start gap-4"
        @submit.prevent="submitAddress"
      >
        <div class="min-w-0 flex-[1_1_220px] xl:max-w-[268px]">
          <input
            v-model="form.address"
            class="h-[45px] w-full rounded-[5px] border bg-white px-5 py-2.5 text-[16px] text-gray-dark placeholder:text-extra-text-color focus:border-extra-text-color"
            :class="inputClass('address')"
            type="text"
            placeholder="Адрес"
            @blur="touchField('address')"
          />
          <p v-if="errors.address" class="mt-1 text-[12px] text-orange">
            {{ errors.address }}
          </p>
        </div>

        <div class="min-w-0 flex-[1_1_220px] xl:max-w-[268px]">
          <input
            v-model="form.contact"
            class="h-[45px] w-full rounded-[5px] border bg-white px-5 py-2.5 text-[16px] text-gray-dark placeholder:text-extra-text-color focus:border-extra-text-color"
            :class="inputClass('contact')"
            type="text"
            placeholder="Контактное лицо"
            @blur="touchField('contact')"
          />
          <p v-if="errors.contact" class="mt-1 text-[12px] text-orange">
            {{ errors.contact }}
          </p>
        </div>

        <div class="min-w-0 flex-[1_1_220px] xl:max-w-[268px]">
          <input
            class="h-[45px] w-full rounded-[5px] border bg-white px-5 py-2.5 text-[16px] text-gray-dark placeholder:text-extra-text-color focus:border-extra-text-color"
            :class="inputClass('phone')"
            type="tel"
            inputmode="tel"
            :value="formattedPhone"
            maxlength="18"
            placeholder="+7(912)415-26-79"
            :aria-invalid="Boolean(errors.phone)"
            :title="errors.phone || undefined"
            @input="onPhoneInput"
            @blur="touchField('phone')"
          />
          <p v-if="errors.phone" class="mt-1 text-[12px] text-orange">
            {{ errors.phone }}
          </p>
        </div>

        <div class="min-w-0 flex-[1_1_220px] xl:max-w-[268px]">
          <input
            v-model="form.locality"
            class="h-[45px] w-full rounded-[5px] border bg-white px-5 py-2.5 text-[16px] text-gray-dark placeholder:text-extra-text-color focus:border-extra-text-color"
            :class="inputClass('locality')"
            type="text"
            placeholder="Нас. пункт"
            @blur="touchField('locality')"
          />
          <p v-if="errors.locality" class="mt-1 text-[12px] text-orange">
            {{ errors.locality }}
          </p>
        </div>

        <div class="min-w-0 flex-[1_1_220px] xl:max-w-[268px]">
          <input
            v-model="form.region"
            class="h-[45px] w-full rounded-[5px] border bg-white px-5 py-2.5 text-[16px] text-gray-dark placeholder:text-extra-text-color focus:border-extra-text-color"
            :class="inputClass('region')"
            type="text"
            placeholder="Регион"
            @blur="touchField('region')"
          />
          <p v-if="errors.region" class="mt-1 text-[12px] text-orange">
            {{ errors.region }}
          </p>
        </div>

        <button
          type="submit"
          class="h-[45px] shrink-0 rounded-[10px] border border-gray-dark bg-white px-8 text-[18px] font-helvetica-400 font-normal text-gray-dark transition-colors active:bg-selected-orange active:border-selected-orange active:text-white cursor-pointer disabled:cursor-not-allowed disabled:border-gray-border disabled:text-gray-light"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? "Добавляем..." : "Добавить" }}
        </button>
      </form>

      <p v-if="submitError" class="text-[14px] text-orange">
        {{ submitError }}
      </p>
      <p v-else-if="submitSuccess" class="text-[14px] text-green-button">
        {{ submitSuccess }}
      </p>
    </div>

    <SettingsAddressTable :rows="addresses" :loading="addressesLoading" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useNuxtApp } from "#imports";
import SettingsNavBar from "~/components/settings/SettingsNavBar.vue";
import SettingsAddressTable from "~/components/settings/SettingsAddressTable.vue";

usePageTitle("Профиль: адреса доставки");

type AddressRow = {
  id: string;
  address: string;
  contact: string;
  phones: string;
  locality: string;
  region: string;
};

type AddressForm = {
  address: string;
  contact: string;
  phone: string;
  locality: string;
  region: string;
};

type AddressField = keyof AddressForm;

const createEmptyForm = (): AddressForm => ({
  address: "",
  contact: "",
  phone: "",
  locality: "",
  region: "",
});

const form = reactive<AddressForm>(createEmptyForm());
const touched = reactive<Record<AddressField, boolean>>({
  address: false,
  contact: false,
  phone: false,
  locality: false,
  region: false,
});
const errors = reactive<Record<AddressField, string>>({
  address: "",
  contact: "",
  phone: "",
  locality: "",
  region: "",
});

const submitError = ref("");
const submitSuccess = ref("");
const isSubmitting = ref(false);

const normalizePhoneDigits = (value: string) => {
  let digits = (value || "").replace(/\D/g, "");
  if (digits.startsWith("8")) digits = `7${digits.slice(1)}`;
  if (digits && !digits.startsWith("7")) digits = `7${digits}`;
  return digits.slice(0, 11);
};

const formatPhoneDisplay = (value: string) => {
  const digits = normalizePhoneDigits(value);
  if (!digits) return "";

  const code = digits.slice(1, 4);
  const first = digits.slice(4, 7);
  const second = digits.slice(7, 9);
  const third = digits.slice(9, 11);

  let result = "+7";
  if (code) result += `(${code}`;
  if (code.length === 3) result += ")";
  if (first) result += first;
  if (second) result += `-${second}`;
  if (third) result += `-${third}`;
  return result;
};

const formatPhoneForApi = (value: string) => formatPhoneDisplay(value);

const formattedPhone = computed(() => formatPhoneDisplay(form.phone));

const validateField = (field: AddressField) => {
  if (field === "phone") {
    const digits = normalizePhoneDigits(form.phone);
    if (!digits) {
      errors.phone = "Введите телефон";
      return;
    }
    if (digits.length !== 11) {
      errors.phone = "Неверный формат телефона";
      return;
    }
    errors.phone = "";
    return;
  }

  const labels: Record<Exclude<AddressField, "phone">, string> = {
    address: "Введите адрес",
    contact: "Введите контактное лицо",
    locality: "Введите населенный пункт",
    region: "Введите регион",
  };

  const value = form[field].trim();
  errors[field] = value ? "" : labels[field as Exclude<AddressField, "phone">];
};

const validateAll = () => {
  (Object.keys(form) as AddressField[]).forEach((field) => {
    touched[field] = true;
    validateField(field);
  });

  return !Object.values(errors).some(Boolean);
};

const touchField = (field: AddressField) => {
  touched[field] = true;
  validateField(field);
};

const inputClass = (field: AddressField) =>
  errors[field] ? "border-orange" : "border-transparent";

const resetForm = () => {
  Object.assign(form, createEmptyForm());
  (Object.keys(touched) as AddressField[]).forEach((field) => {
    touched[field] = false;
    errors[field] = "";
  });
};

const onPhoneInput = (event: Event) => {
  const target = event.target as HTMLInputElement | null;
  const digits = normalizePhoneDigits(target?.value ?? "");
  form.phone = digits;
  if (touched.phone) validateField("phone");
  if (target) target.value = formatPhoneDisplay(digits);
};

const addresses = ref<AddressRow[]>([]);
const addressesLoading = ref(true);

const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp.$useOneCApi as (
  action: string,
  payload?: Record<string, any>,
) => Promise<any>;

const mapAddressRow = (item: any, index: number): AddressRow => {
  const address = String(item?.Адрес ?? "").trim();
  const contact = String(item?.КонтактноеЛицо ?? "").trim();
  const phones = String(item?.Телефоны ?? "").trim();
  const locality = String(item?.НаселенныйПункт ?? "").trim();
  const region = String(item?.Регион ?? "").trim();

  return {
    id: `${address}-${contact}-${phones}-${locality}-${region}-${index}`,
    address,
    contact,
    phones,
    locality,
    region,
  };
};

const fetchAddresses = async () => {
  addressesLoading.value = true;
  try {
    const res = await $useOneCApi("getAddresses");
    const list = Array.isArray(res) ? res : [];
    addresses.value = list.map(mapAddressRow);
  } catch (error) {
    console.error("Не удалось загрузить адреса доставки", error);
    addresses.value = [];
  } finally {
    addressesLoading.value = false;
  }
};

const submitAddress = async () => {
  submitError.value = "";
  submitSuccess.value = "";

  if (!validateAll()) return;

  isSubmitting.value = true;

  try {
    await $useOneCApi("addAddress", {
      dataInJSON: JSON.stringify([
        form.address.trim(),
        form.contact.trim(),
        formatPhoneForApi(form.phone),
        form.locality.trim(),
        form.region.trim(),
      ]),
    });

    resetForm();
    submitSuccess.value = "Адрес доставки добавлен";
    await fetchAddresses();
  } catch (error) {
    console.error("Не удалось добавить адрес доставки", error);
    submitError.value =
      "Не удалось добавить адрес доставки. Попробуйте еще раз.";
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  fetchAddresses();
});
</script>
